import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../api.js";
import { markAppointmentNoShow, undoAppointmentNoShow } from "../booking/appointmentStatus.js";
import { idempotencyKey } from "../booking/api.js";
import { TutorCancelLesson } from "../booking/TutorCancelLesson.js";
import { bookingSystemDisplayText } from "../booking/staffDisplayCopy.js";
import {
  ameliaAttendance,
  getLessonOutcome,
  type AmeliaAttendance,
  type LessonOutcome,
} from "../booking/lessonOutcome.js";
import { Drawer } from "../ui/Dialog.js";

/**
 * What Amelia says happened to this lesson, and the only ways to change it.
 *
 * "Did not attend" and "Cancelled" are not notes: they change the booking and
 * can change money. They are therefore never saved from the note itself. This
 * panel reads WMF-SIte's live outcome, offers only what that read says is safe
 * for this participant, and leaves the note's own attendance to follow Amelia.
 */
export interface LessonAttendanceProps {
  ameliaAppointmentId: number | null;
  /** This pupil's own booking within the appointment; without it nothing is proved. */
  ameliaBookingId: number | null;
  startsAt: string;
  studentName: string;
  serviceName: string | null;
  /** True while the note itself is saving, so the two cannot interleave. */
  disabled?: boolean;
  /** Refresh only the live outcome, preserving the write-up's unsaved fields. */
  refreshKey?: number;
  /** Reported on every read and after every confirmed change. */
  onAttendance: (state: AmeliaAttendance | "unknown") => void;
  /** Injected so the "lesson has started" rule can be tested. */
  now?: () => number;
}

type Pending = "no-show" | "undo" | null;

const UNCONFIRMED =
  "The change has not been confirmed. Check the lesson’s current status before trying again.";

/** A refusal that changed nothing, or a response that may follow a write. */
function failureOf(cause: unknown): { message: string; uncertain: boolean } {
  if (cause instanceof ApiError) {
    if (cause.status === 0 || cause.status >= 500) return { message: UNCONFIRMED, uncertain: true };
    return { message: bookingSystemDisplayText(cause.message), uncertain: false };
  }
  return { message: UNCONFIRMED, uncertain: true };
}

const STATE_COPY: Record<AmeliaAttendance, string> = {
  active: "Booked",
  "no-show": "Did not attend (no-show)",
  canceled: "Cancelled",
  other: "Not an active booking",
};

export function LessonAttendance({
  ameliaAppointmentId,
  ameliaBookingId,
  startsAt,
  studentName,
  serviceName,
  disabled = false,
  refreshKey = 0,
  onAttendance,
  now = Date.now,
}: LessonAttendanceProps) {
  const [outcome, setOutcome] = useState<LessonOutcome | null>(null);
  const [loading, setLoading] = useState(ameliaAppointmentId !== null);
  const [readProblem, setReadProblem] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [needsCheck, setNeedsCheck] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const report = useRef(onAttendance);
  report.current = onAttendance;
  const scopeKey = `${ameliaAppointmentId ?? "none"}:${ameliaBookingId ?? "none"}`;
  const activeScope = useRef(scopeKey);
  activeScope.current = scopeKey;
  const readRequest = useRef(0);
  // One key per logical attempt: a retry after an unconfirmed answer reuses it.
  const attempt = useRef<{ kind: Exclude<Pending, null>; key: string } | null>(null);

  const read = useCallback(async () => {
    const request = ++readRequest.current;
    const requestedScope = scopeKey;
    setOutcome(null);
    setReadProblem(null);
    setNeedsCheck(false);
    report.current("unknown");
    if (ameliaAppointmentId === null) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const next = await getLessonOutcome(ameliaAppointmentId);
      if (readRequest.current !== request || activeScope.current !== requestedScope) return;
      setOutcome(next);
      setReadProblem(null);
      setNeedsCheck(false);
      report.current(ameliaAttendance(next, ameliaBookingId));
    } catch (cause: unknown) {
      if (readRequest.current !== request || activeScope.current !== requestedScope) return;
      setOutcome(null);
      setReadProblem(
        cause instanceof ApiError && cause.status < 500 && cause.status !== 0
          ? bookingSystemDisplayText(cause.message)
          : "The booking system could not confirm the lesson status.",
      );
      report.current("unknown");
    } finally {
      if (readRequest.current === request && activeScope.current === requestedScope) setLoading(false);
    }
  }, [ameliaAppointmentId, ameliaBookingId, scopeKey]);

  useEffect(() => {
    attempt.current = null;
    setPending(null);
    setProblem(null);
    setNeedsCheck(false);
    setCancelling(false);
    setCancelBusy(false);
    void read();
  }, [read, refreshKey]);

  async function change(kind: Exclude<Pending, null>) {
    if (ameliaAppointmentId === null || pending) return;
    if (attempt.current?.kind !== kind) attempt.current = { kind, key: idempotencyKey() };
    const key = attempt.current.key;
    setPending(kind);
    setProblem(null);
    try {
      const result =
        kind === "no-show"
          ? await markAppointmentNoShow(ameliaAppointmentId, key)
          : await undoAppointmentNoShow(ameliaAppointmentId, key);
      const expected = kind === "no-show" ? "no-show" : "approved";
      if (result?.appointment?.id !== ameliaAppointmentId || result.appointment.status !== expected) {
        setProblem(UNCONFIRMED);
        setNeedsCheck(true);
        return;
      }
      attempt.current = null;
      await read();
    } catch (cause: unknown) {
      const failure = failureOf(cause);
      setProblem(failure.message);
      if (failure.uncertain) setNeedsCheck(true);
      else attempt.current = null;
    } finally {
      setPending(null);
    }
  }

  if (ameliaAppointmentId === null) return null;

  const state = outcome ? ameliaAttendance(outcome, ameliaBookingId) : null;
  const started = Date.parse(startsAt) <= now();
  const busy = disabled || pending !== null || loading || cancelBusy;
  const actionsLocked = busy || needsCheck;
  const sharedOrUnknown = outcome !== null && outcome.mutation_scope !== "single_participant";

  return (
    <section className="lesson-notes-attendance lesson-notes-amelia" aria-label="Lesson status">
      <h2>Lesson status</h2>
      <p className="small">
        {loading && outcome === null ? (
          "Checking lesson status…"
        ) : state === "unknown" ? (
          "The booking system could not confirm this pupil’s place in the lesson, so the note will not change it."
        ) : state !== null ? (
          <>
            The booking system shows this lesson as <strong>{STATE_COPY[state]}</strong>.
          </>
        ) : (
          readProblem
        )}
      </p>

      {sharedOrUnknown && (
        <p className="small muted">
          This lesson has more than one participant, or the booking system could not confirm who is booked, so
          attendance cannot be changed from a note. Use the diary.
        </p>
      )}

      {outcome !== null && !sharedOrUnknown && state !== "unknown" && (
        <div className="lesson-notes-actions">
          {outcome.can_mark_no_show && (
            <button
              type="button"
              className="quiet"
              disabled={actionsLocked || !started}
              onClick={() => void change("no-show")}
            >
              {pending === "no-show" ? "Recording…" : "Record did not attend"}
            </button>
          )}
          {outcome.can_mark_no_show && !started && (
            <span className="small muted">Available once the lesson has started.</span>
          )}
          {outcome.can_undo_no_show && (
            <button
              type="button"
              className="quiet"
              disabled={actionsLocked}
              onClick={() => void change("undo")}
            >
              {pending === "undo" ? "Undoing…" : "Undo did not attend"}
            </button>
          )}
          {outcome.can_cancel && (
            <button
              type="button"
              className="quiet"
              disabled={actionsLocked}
              onClick={() => setCancelling(true)}
            >
              Cancel this lesson…
            </button>
          )}
        </div>
      )}

      {problem && (
        <div className="error" role="alert">
          {problem}
        </div>
      )}
      {(needsCheck || readProblem !== null) && (
        <button type="button" className="quiet" disabled={loading || pending !== null} onClick={() => void read()}>
          Check lesson status
        </button>
      )}

      {cancelling && (
        <Drawer
          eyebrow="Appointment"
          title={studentName}
          onClose={() => {
            if (cancelBusy) return;
            setCancelling(false);
            void read();
          }}
          busy={cancelBusy}
          closeLabel="Close cancellation"
        >
          <TutorCancelLesson
            appointmentId={String(ameliaAppointmentId)}
            studentName={studentName}
            startsAt={startsAt}
            serviceName={serviceName}
            onBusyChange={setCancelBusy}
            onClose={() => {
              setCancelling(false);
              setCancelBusy(false);
              void read();
            }}
          />
        </Drawer>
      )}
    </section>
  );
}
